import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/atoms/Sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/atoms/Collapsible';
import { cn } from '@/utils/tailwind';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SidebarItem } from '@/types/nav';
import { useLocation } from 'react-router-dom';
import { useSidebar } from '@/components/atoms/Sidebar';
import { useCallback } from 'react';

export function HasSubMenuItem({ item, isActive }: { item: SidebarItem; isActive?: boolean }) {
  const Icon = item.iconOutline;
  const location = useLocation();
  const { state } = useSidebar();

  const firstSubUrl = item.subItems?.[0]?.url;

  // 클릭 핸들러: 접힘 상태에서 첫번째 서브메뉴로 이동
  const handleMenuButtonClick = useCallback(
    (e: React.MouseEvent) => {
      if (state === 'collapsed' && firstSubUrl) {
        e.preventDefault();
        if (typeof firstSubUrl === 'string' && firstSubUrl.startsWith('http')) {
          window.open(firstSubUrl, '_blank');
        } else {
          window.location.href = firstSubUrl;
        }
      }
    },
    [state, firstSubUrl],
  );

  return (
    <Collapsible defaultOpen={isActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            className={`group/a h-[48px] w-full gap-[14px] px-[13px] py-[12px] ${
              state === 'expanded' ? 'h-[48px]' : 'h-[40px]'
            } `}
            variant="main"
            onClick={handleMenuButtonClick}
            tooltip={{ children: <SidebarTooltipSubItems item={item} /> }}
            isActive={isActive}
          >
            {Icon && <Icon className={cn('h-6 w-6 fill-gr-500', isActive ? 'fill-white' : '')} />}
            <span className="Body3S15">{item.title}</span>
            <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:-rotate-180" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className={`${state === 'expanded' && 'my-[20px]'}`}>
          <SidebarMenuSub>
            {item.subItems?.map((sub, index) => {
              let isSubActive = false;

              // "더보기" 메뉴의 서브 메뉴라면 ctab만 비교
              if (
                item.url === '/more' &&
                typeof sub.url === 'string' &&
                sub.url.startsWith('/more')
              ) {
                const params = new URLSearchParams(location.search);
                const ctab = params.get('ctab');
                const subParams = new URLSearchParams(sub.url.split('?')[1] || '');
                isSubActive = subParams.get('ctab') === ctab;
              }
              // "주문 내역" 활성화: order-details 경로 + orderTab 파라미터가 있으면 활성
              else if (item.url === '/orders' && sub.url.startsWith('/orders/order-details')) {
                isSubActive =
                  location.pathname === '/orders/order-details' &&
                  new URLSearchParams(location.search).has('orderTab');
              }
              // "리뷰 관리" 활성화: /orders/reviews 경로만 맞으면 활성
              else if (item.url === '/orders' && sub.url.startsWith('/orders/reviews')) {
                isSubActive = location.pathname === '/orders/reviews';
              }
              // "문의 관리" 활성화: /orders/inquiries 경로만 맞으면 활성
              else if (item.url === '/orders' && sub.url.startsWith('/orders/inquiries')) {
                isSubActive = location.pathname === '/orders/inquiries';
              }
              // 외부 링크
              else if (typeof sub.url === 'string' && sub.url.startsWith('http')) {
                isSubActive = location.pathname + location.search === sub.url;
              }
              // 그 외 메뉴
              else {
                isSubActive = location.pathname + location.search === sub.url;
              }

              return (
                <SidebarMenuSubItem key={index}>
                  <SidebarMenuSubButton asChild isActive={isSubActive}>
                    {typeof sub.url === 'string' && sub.url.startsWith('http') ? (
                      <a
                        href={sub.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-gr-600 Body3S15 hover:text-gr-700/90"
                      >
                        <span>{sub.title}</span>
                      </a>
                    ) : (
                      <a
                        href={sub.url}
                        className={`flex items-center gap-1  bg-white  Body3S15 ${
                          isSubActive ? ' text-gr-700 ' : 'text-gr-600  hover:text-gr-700/90'
                        }`}
                      >
                        <span>{sub.title}</span>
                      </a>
                    )}
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarTooltipSubItems({ item }: { item: SidebarItem }) {
  return (
    <div
      className="Body9M14 flex flex-col 
    gap-1 rounded-xl border bg-white p-[8px] py-[20px] shadow-lg "
    >
      {item.subItems?.map((sub, index) => {
        const isExternal = typeof sub.url === 'string' && sub.url.startsWith('http');
        return isExternal ? (
          <a
            key={index}
            href={sub.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block whitespace-nowrap rounded-xl py-[8px] pl-[8px] pr-[32px] text-left text-sm text-gr-800 transition-colors hover:bg-gr-200"
          >
            {sub.title}
          </a>
        ) : (
          <a
            key={index}
            href={sub.url}
            className="block whitespace-nowrap rounded-xl py-[8px] pl-[8px] pr-[32px] text-left text-sm text-gr-800 transition-colors hover:bg-gr-200"
          >
            {sub.title}
          </a>
        );
      })}
    </div>
  );
}
